import { useMutation } from '@apollo/client'
import { deserializePin, parsePinToParts } from '@faceup/crypto'
import { maskEmail } from '@faceup/utils'
import { useContext, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../../../../__generated__'
import { EmailSubscriptionForm } from '../../../../../Components/EmailSubscriptionForm'
import { UserContext } from '../../../../../Contexts/UserContext'
import { useCustomValues } from '../../../../../hooks/useCustomValues'
import { usePreLogin } from '../../../../../hooks/usePreLogin'

const fragments = {
  NotifyMe_publicReportSource: graphql(`
    fragment NotifyMe_publicReportSource on PublicReportSource {
      id
      config {
        ... on FormReportSourceConfiguration {
          id
          emailNotificationEnabled
        }
      }
    }
  `),
}

const mutations = {
  notifyMe: graphql(`
    mutation NotifyMeMutation($input: EditEmailCompanyVictimAnonymousInput!) {
      editEmailCompanyVictimAnonymous(input: $input) {
        companyVictim {
          id
        }
      }
    }
  `),
  resendVerification: graphql(`
    mutation NotifyMeResendVerificationMutation {
      resendVictimEmailVerification {
        result
        retryAfterSeconds
        companyVictim {
          id
          emailMasked
          isEmailVerified
        }
      }
    }
  `),
}

type NotifyMeProps = {
  publicReportSource: FragmentType<typeof fragments.NotifyMe_publicReportSource>
  reportPin: string
  isLoggedIn: boolean
}

export const NotifyMe = ({
  publicReportSource: _publicReportSource,
  reportPin,
  isLoggedIn,
}: NotifyMeProps) => {
  const publicReportSource = getFragmentData(
    fragments.NotifyMe_publicReportSource,
    _publicReportSource
  )
  const { reportSource } = useContext(UserContext)
  const preLogin = usePreLogin()
  const [insertedEmail, setInsertedEmail] = useState<string | null>(null)
  const customValues = useCustomValues()

  const [notify] = useMutation(mutations.notifyMe)
  const [resendVerification] = useMutation(mutations.resendVerification)

  const isEmailNotificationEnabled =
    publicReportSource.config?.__typename !== 'FormReportSourceConfiguration' ||
    (publicReportSource.config?.emailNotificationEnabled &&
      customValues?.emailNotificationEnabled === undefined) ||
    customValues?.emailNotificationEnabled === true

  if (!isEmailNotificationEnabled) {
    return null
  }

  const callNotifyMutation = async (email: string | null) => {
    const pin = deserializePin(reportPin)
    const { identity, version } = parsePinToParts(pin)
    const { passwordKeyPrehash } = await preLogin.performPreLogin(reportPin)
    const { data } = await notify({
      variables: {
        input: {
          email,
          reportPassword: reportSource?.password,
          passwordPrehash: passwordKeyPrehash,
          version,
          identity,
          organizationalUnitId: reportSource?.institutionId ?? '',
        },
      },
    })
    return Boolean(data)
  }

  return (
    <EmailSubscriptionForm
      maskedEmail={insertedEmail ? maskEmail(insertedEmail) : null}
      isEmailVerified={false}
      onSubmit={async email => {
        const success = await callNotifyMutation(email)
        if (success) {
          setInsertedEmail(email)
        }
        return success
      }}
      onUnsubscribe={async () => {
        const success = await callNotifyMutation(null)
        if (success) {
          setInsertedEmail(null)
        }
        return success
      }}
      onResend={
        isLoggedIn
          ? async () => {
              const { data } = await resendVerification()
              const payload = data?.resendVictimEmailVerification
              if (!payload) {
                throw new Error('Empty resendVictimEmailVerification payload')
              }
              return {
                result: payload.result,
                retryAfterSeconds: payload.retryAfterSeconds ?? null,
              }
            }
          : undefined
      }
    />
  )
}
